import { HistoryToggleOff, Loop } from '@mui/icons-material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { NodeProps, Position } from 'reactflow';

import { Hexagon } from './Hexagon';
import { SOURCE_HANDLE_IDENTIFIER } from '../../../../constants';
import { LABWARE_ITEM_ALLOWLIST } from '../../../../errorAllowOrBlockLists';
import { selectLabwareColorIndexById } from '../../../../features/selectors';
import {
  selectLabwareProperties,
  selectLabwareStartingInstrumentDisplayNames,
} from '../../../../features/workflow/selectors';
import { selectWorkflowLevelErrorsForLabwareId } from '../../../../features/workflowJsonSelectors/workflowLevelErrors';
import { useAppSelector } from '../../../../hooks';
import type { LabwareProperties } from '../../../../types';
import { getColorFromIndex } from '../../../../utils';
import { Icon } from '../../../customIcons/labware';
import Handle from '../../Handle';
import NodeIndicators from '../../NodeIndicators';
import { Props as NodeIndicatorProps } from '../../NodeIndicators/NodeIndicator';

// const iconColumnStyle: CSSProperties = {
//   position: 'absolute',
//   right: -38,
//   top: 80,
//   display: 'flex',
//   flexDirection: 'column',
// };

// const iconStyle: CSSProperties = {
//   margin: '0.2rem 0',
// };

type Props = {
  id: string;
  color: string;
  instrumentName?: string;
  properties: LabwareProperties;
  selected: boolean;
  indicators: JSX.Element | null;
};

export const LabwareNode = ({
  id,
  color,
  instrumentName,
  selected,
  properties: { nickname, labwareType },
  indicators,
}: Props) => {
  const [isHovered, setIsHovered] = useState(false);

  const NODE_SIZE = 100;

  return (
    <Box
      position="relative"
      width={NODE_SIZE}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box
        position="absolute"
        sx={{ transform: 'translate(0, -100%)' }}
        bgcolor="rgba(248, 248, 248, 0.8)"
        borderRadius={1}
        py={1}
        px={2}
      >
        <Typography variant="h6">{nickname || labwareType}</Typography>
        <Typography
          variant="body2"
          sx={[instrumentName == undefined && { fontStyle: 'italic' }]}
        >
          {instrumentName ?? '(No starting location)'}
        </Typography>
        {indicators}
      </Box>

      <Box position="relative">
        <Box position="relative" top={4}>
          <Hexagon
            color={color}
            isSelected={selected}
            isHovered={isHovered}
            size={NODE_SIZE}
          />
        </Box>
        <Box
          display="flex"
          position="absolute"
          left="0"
          right="0"
          top="0"
          bottom="0"
          alignItems="center"
          justifyContent="center"
        >
          <Icon
            sx={{ transform: ' scale(1.4)' }}
            htmlColor={color}
            iconType={labwareType}
            fontSize="large"
          />
        </Box>
      </Box>

      <Handle
        id={`${SOURCE_HANDLE_IDENTIFIER}.${id}`}
        type="source"
        position={Position.Bottom}
        style={{ borderColor: color }}
      />

      {/* TODO(IL, 2024-03-12): these icons are placeholders, need real icons */}
      {/* NOTE(IL, 2024-04-04): hidden for user test 1 */}
      {/* <Box sx={iconColumnStyle}>
        <OpacityIcon sx={iconStyle} />
        <NoteIcon sx={iconStyle} />
        <LoopIcon sx={iconStyle} />
      </Box> */}
    </Box>
  );
};

export const ConnectedLabwareNode = ({ id, selected }: NodeProps) => {
  const labwareProperties = useAppSelector(selectLabwareProperties)[id];
  const colorIndex = useAppSelector(selectLabwareColorIndexById)[id] ?? 0;
  const instrumentName = useAppSelector(
    selectLabwareStartingInstrumentDisplayNames
  )[id];
  const workflowLevelLabwareErrors = useAppSelector((state) =>
    selectWorkflowLevelErrorsForLabwareId(state, id)
  );

  if (labwareProperties === undefined) {
    // NOTE(IL, 2024-03-18): nodes can be deleted from redux before they're deleted
    // from the GraphEditor `nodes` component state,
    // maybe there's opportunity for cleanup in GraphEditor??
    return null;
  }

  const { maxIdleTime, singleOrMultiUse } = labwareProperties;

  const indicators: NodeIndicatorProps[] = [];

  // relevant workflow-level errors in allowlist
  const errorMessages: string[] = [];
  for (const err of workflowLevelLabwareErrors) {
    const message = err.error.message;

    if (LABWARE_ITEM_ALLOWLIST.includes(message))
      errorMessages.push(err.description);
  }
  if (errorMessages.length > 0) {
    indicators.push({
      icon: WarningAmberIcon,
      iconColor: 'error',
      message: errorMessages,
    });
  }

  // data indicators (not related to errors)
  if (singleOrMultiUse === 'multi') {
    indicators.push({
      icon: Loop,
      message: 'Multi-use labware',
    });
  }

  if (maxIdleTime) {
    indicators.push({
      icon: HistoryToggleOff,
      message: 'The maximum idle time for this labware has been set up.',
    });
  }

  return (
    <LabwareNode
      id={id}
      color={getColorFromIndex(colorIndex)}
      instrumentName={instrumentName}
      properties={labwareProperties}
      selected={selected}
      indicators={<NodeIndicators indicators={indicators} />}
    />
  );
};
